import { useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Breadcrumbs,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { huHU } from '@mui/x-data-grid/locales';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockIcon from '@mui/icons-material/Lock';

import { CheckboxUI, ColorCircleUi, ColorPaletteUI, TooltipUI } from '../Interface';

const defaultFolderColor = '#ed6c02';
const rootFolder = { key: 'root', id: null, name: 'Főkönyvtár', children: [] };

const getFolderKey = (id) => `folder-${id}`;

const createFolderTree = (folders) => {
  const root = { ...rootFolder, children: [] };
  const folderMap = new Map(
    folders.map((folder) => [
      String(folder.id),
      {
        ...folder,
        key: getFolderKey(folder.id),
        children: [],
      },
    ])
  );

  folderMap.forEach((folder) => {
    const parent = folder.parentId == null ? root : folderMap.get(String(folder.parentId));
    (parent ?? root).children.push(folder);
  });

  const sortChildren = (folder) => {
    folder.children.sort((firstFolder, secondFolder) =>
      firstFolder.name.localeCompare(secondFolder.name, 'hu', { sensitivity: 'base' })
    );
    folder.children.forEach(sortChildren);
  };
  sortChildren(root);

  return root;
};

const findFolderPath = (folder, targetKey, path = []) => {
  const currentPath = [...path, folder];
  if (folder.key === targetKey) return currentPath;

  for (const child of folder.children) {
    const childPath = findFolderPath(child, targetKey, currentPath);
    if (childPath) return childPath;
  }

  return null;
};

const FolderTreeItem = ({ folder, selectedFolderKey, onSelect, level = 0 }) => {
  const [open, setOpen] = useState(folder.key === 'root');
  const hasChildren = folder.children.length > 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 36,
          pl: level * 2,
          pr: 1,
          borderRadius: 1,
          bgcolor: selectedFolderKey === folder.key ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <IconButton
          size="small"
          aria-label={open ? 'Mappa összecsukása' : 'Mappa kibontása'}
          disabled={!hasChildren}
          onClick={() => setOpen((previousOpen) => !previousOpen)}
          sx={{ mr: 0.25, visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Button
          color="inherit"
          onClick={() => onSelect(folder.key)}
          startIcon={
            <Stack direction="row" spacing={1}>
              {folder.protected && (
                <TooltipUI title="Védett mappa">
                  <LockIcon fontSize="small" color="error" />
                </TooltipUI>
              )}
              <FolderIcon sx={{ color: folder.color ?? defaultFolderColor }} fontSize="small" />
            </Stack>
          }
          sx={{
            minWidth: 0,
            flex: 1,
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: selectedFolderKey === folder.key ? 600 : 400,
          }}
        >
          <Typography variant="body2" noWrap>
            {folder.name}
          </Typography>
        </Button>
      </Box>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {folder.children.map((child) => (
            <FolderTreeItem
              key={child.key}
              folder={child}
              selectedFolderKey={selectedFolderKey}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const FolderColorPicker = ({ color, onChange, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography>Mappa színe:</Typography>
      <Box>
        <ColorCircleUi
          color={color}
          size={20}
          onClick={disabled ? undefined : (event) => setAnchorEl(event.currentTarget)}
        />
      </Box>
      <ColorPaletteUI
        anchorEl={anchorEl}
        handlePopoverClose={() => setAnchorEl(null)}
        onClick={(selectedColor) => {
          onChange(selectedColor);
          setAnchorEl(null);
        }}
      />
    </Stack>
  );
};

const FolderOptions = ({ color, onColorChange, disabled, isProtected, onProtectedChange }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
    <FolderColorPicker color={color} onChange={onColorChange} disabled={disabled} />

    <CheckboxUI
      sx={{ m: 0, p: 0, pr: 0.5 }}
      label="Védett"
      checked={isProtected}
      onChange={(e) => onProtectedChange(e.target.checked)}
    />
  </Stack>
);

const FolderTemplateFolderEditor = ({ disabled }) => {
  const { values, setFieldValue } = useFormikContext();
  const folders = values.folderTemplateItem ?? [];
  const [selectedFolderKey, setSelectedFolderKey] = useState('root');
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState(defaultFolderColor);
  const [folderProtected, setFolderProtected] = useState(false);
  const [folderNameError, setFolderNameError] = useState('');
  const [folderMenuAnchor, setFolderMenuAnchor] = useState(null);
  const [folderMenuRow, setFolderMenuRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const folderTree = useMemo(() => createFolderTree(folders), [folders]);
  const selectedPath = useMemo(
    () => findFolderPath(folderTree, selectedFolderKey) ?? [folderTree],
    [folderTree, selectedFolderKey]
  );
  const selectedFolder = selectedPath[selectedPath.length - 1];

  useEffect(() => {
    if (!findFolderPath(folderTree, selectedFolderKey)) setSelectedFolderKey('root');
  }, [folderTree, selectedFolderKey]);

  const closeFolderDialog = () => {
    setFolderDialogOpen(false);
    setFolderToEdit(null);
    setFolderName('');
    setFolderColor(defaultFolderColor);
    setFolderProtected(false);
    setFolderNameError('');
  };

  const openCreateDialog = () => {
    setFolderToEdit(null);
    setFolderName('');
    setFolderColor(defaultFolderColor);
    setFolderNameError('');
    setFolderDialogOpen(true);
  };

  const openEditDialog = (folder) => {
    setFolderToEdit(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color);
    setFolderProtected(folder.protected);
    setFolderNameError('');
    setFolderDialogOpen(true);
  };

  const saveFolder = async () => {
    const name = folderName.trim();
    if (!name) {
      setFolderNameError('A mappa neve kötelező');
      return;
    }

    const siblings = folderToEdit
      ? folders.filter((folder) => String(folder.parentId ?? '') === String(folderToEdit.parentId ?? ''))
      : selectedFolder.children;
    const duplicateName = siblings.some(
      (folder) =>
        String(folder.id) !== String(folderToEdit?.id) &&
        folder.name.localeCompare(name, 'hu', { sensitivity: 'base' }) === 0
    );
    if (duplicateName) {
      setFolderNameError('Ebben a mappában már létezik ilyen nevű mappa');
      return;
    }

    const updatedFolders = folderToEdit
      ? folders.map((folder) =>
          String(folder.id) === String(folderToEdit.id)
            ? { ...folder, name, color: folderColor, protected: folderProtected }
            : folder
        )
      : [
          ...folders,
          {
            id: uuidv4(),
            name,
            color: folderColor,
            protected: folderProtected,
            parentId: selectedFolder.id,
          },
        ];

    await setFieldValue('folderTemplateItem', updatedFolders);
    closeFolderDialog();
  };

  const deleteFolder = async () => {
    if (!folderMenuRow) return;

    const folderIdsToDelete = new Set([String(folderMenuRow.id)]);
    let foundChild = true;
    while (foundChild) {
      foundChild = false;
      folders.forEach((folder) => {
        if (folder.parentId != null && folderIdsToDelete.has(String(folder.parentId))) {
          const folderId = String(folder.id);
          if (!folderIdsToDelete.has(folderId)) {
            folderIdsToDelete.add(folderId);
            foundChild = true;
          }
        }
      });
    }

    await setFieldValue(
      'folderTemplateItem',
      folders.filter((folder) => !folderIdsToDelete.has(String(folder.id)))
    );
    if (folderIdsToDelete.has(String(selectedFolder.id))) setSelectedFolderKey('root');
    setDeleteDialogOpen(false);
    setFolderMenuRow(null);
  };

  const rows = selectedFolder.children.map((folder) => ({ ...folder, rowId: folder.key, typeName: 'Mappa' }));
  const columns = [
    {
      field: 'name',
      headerName: 'Név',
      minWidth: 180,
      flex: 1,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: '100%', height: '100%' }}>
          {row.protected && (
            <TooltipUI title="Védett mappa">
              <LockIcon fontSize="small" color="error" />
            </TooltipUI>
          )}
          <FolderIcon sx={{ color: row.color }} fontSize="small" />
          <Typography variant="body2" noWrap>
            {row.name}
          </Typography>
        </Stack>
      ),
    },
    { field: 'typeName', headerName: 'Típus', width: 140, minWidth: 90 },
    {
      field: 'actions',
      headerName: '',
      width: 52,
      minWidth: 52,
      maxWidth: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          disabled={disabled}
          aria-label={`${row.name} műveletei`}
          onClick={(event) => {
            event.stopPropagation();
            setFolderMenuAnchor(event.currentTarget);
            setFolderMenuRow(row);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, minHeight: 480, width: '100%', px: 1.5, pb: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          width: { xs: '100%', md: 280 },
          flex: { xs: '1 1 100%', md: '0 0 280px' },
          maxHeight: { xs: 240, md: 480 },
          p: 1.5,
          overflow: 'auto',
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
          Mappák
        </Typography>
        <FolderTreeItem folder={folderTree} selectedFolderKey={selectedFolderKey} onSelect={setSelectedFolderKey} />
      </Paper>

      <Paper variant="outlined" sx={{ minWidth: 0, flex: '1 1 500px', overflow: 'hidden' }}>
        <Box
          sx={{
            minHeight: 58,
            px: 2,
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ minWidth: 0, flex: '1 1 240px' }}>
            {selectedPath.map((folder, index) =>
              index === selectedPath.length - 1 ? (
                <Typography key={folder.key} color="primary" variant="body2" fontWeight={600}>
                  {folder.name}
                </Typography>
              ) : (
                <Link
                  key={folder.key}
                  component="button"
                  type="button"
                  underline="hover"
                  color="text.secondary"
                  variant="body2"
                  onClick={() => setSelectedFolderKey(folder.key)}
                >
                  {folder.name}
                </Link>
              )
            )}
          </Breadcrumbs>
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderOutlinedIcon />}
            disabled={disabled}
            onClick={openCreateDialog}
            sx={{ textTransform: 'none' }}
          >
            Új mappa
          </Button>
        </Box>

        <Box sx={{ height: 365, width: '100%' }}>
          <DataGridPremium
            rows={rows}
            columns={columns}
            getRowId={(row) => row.rowId}
            disableRowSelectionOnClick
            hideFooter
            localeText={huHU.components.MuiDataGrid.defaultProps.localeText}
            onRowDoubleClick={({ row }) => setSelectedFolderKey(row.key)}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">A mappa üres</Typography>
                </Box>
              ),
            }}
            sx={{ border: 0, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
        >
          {rows.length} elem
        </Typography>
      </Paper>

      <Menu anchorEl={folderMenuAnchor} open={Boolean(folderMenuAnchor)} onClose={() => setFolderMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            openEditDialog(folderMenuRow);
            setFolderMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Szerkesztés
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            setFolderMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Törlés
        </MenuItem>
      </Menu>

      <Dialog open={folderDialogOpen} onClose={closeFolderDialog} fullWidth maxWidth="xs">
        <DialogTitle>{folderToEdit ? 'Mappa szerkesztése' : 'Új mappa'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Mappa neve"
            value={folderName}
            error={Boolean(folderNameError)}
            helperText={folderNameError}
            onChange={(event) => {
              setFolderName(event.target.value);
              setFolderNameError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') saveFolder();
            }}
          />
          <FolderOptions
            color={folderColor}
            onColorChange={setFolderColor}
            disabled={disabled}
            isProtected={folderProtected}
            onProtectedChange={setFolderProtected}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFolderDialog}>Mégse</Button>
          <Button variant="contained" onClick={saveFolder}>
            {folderToEdit ? 'Mentés' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Mappa törlése</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Biztosan törölni szeretné a(z) „{folderMenuRow?.name}” mappát és annak teljes tartalmát?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Mégse</Button>
          <Button color="error" variant="contained" onClick={deleteFolder}>
            Törlés
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FolderTemplateFolderEditor;
